import { useEffect, useMemo, useRef, useState } from "react";
import { ParentSize } from "@visx/responsive";
import { Group } from "@visx/group";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { scaleLinear, scaleTime } from "@visx/scale";
import { LinearGradient } from "@visx/gradient";
import { useTooltip, useTooltipInPortal, defaultStyles } from "@visx/tooltip";
import { bisector } from "d3-array";
import { area as d3area, line as d3line, curveMonotoneX } from "d3-shape";
import { ChartCard, Skeleton, AnimatedNumber } from "@/components/reusable";
import { fetchKbGrowthSeries } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { KbGrowthPoint } from "@/types/analytics";

const tooltipStyles = {
  ...defaultStyles,
  background: "white",
  border: "1px solid rgba(0,0,0,0.14)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  fontSize: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  borderRadius: 8,
  padding: "8px 12px",
};

export function KbGrowthChart() {
  const filters = useAnalyticsFilters();
  const [data, setData] = useState<KbGrowthPoint[] | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    fetchKbGrowthSeries(filters).then((d) => {
      if (active) setData(d);
    });
    return () => {
      active = false;
    };
  }, [filters]);

  const latest = data?.[data.length - 1]?.totalMappings ?? 0;
  const earliest = data?.[0]?.totalMappings ?? 0;
  const added = Math.max(0, latest - earliest);

  return (
    <ChartCard
      title="Knowledge base growth"
      subtitle="Cumulative known mappings across all schemas"
      minHeight={380}
      headerRight={
        data ? (
          <div className="text-right">
            <AnimatedNumber
              value={latest}
              format={(v) => Math.round(v).toLocaleString()}
              className="text-[14px] font-semibold text-[var(--color-text-primary)]"
              style={{ fontFamily: "var(--font-mono)" }}
            />
            <div className="text-[10px] text-[var(--color-brand-primary-dark)] uppercase tracking-wide">
              +
              <AnimatedNumber value={added} format={(v) => Math.round(v).toLocaleString()} /> over
              window
            </div>
          </div>
        ) : null
      }
    >
      {!data ? (
        <Skeleton height="h-full" className="min-h-[200px]" />
      ) : data.length === 0 ? (
        <div className="py-10 text-center text-[12px] text-[var(--color-text-tertiary)]">
          No data in selected range.
        </div>
      ) : (
        <div className="h-full w-full">
          <ParentSize>
            {({ width, height }) =>
              width > 0 && height > 0 ? (
                <GrowthChart data={data} width={width} height={height} />
              ) : null
            }
          </ParentSize>
        </div>
      )}
    </ChartCard>
  );
}

interface Parsed {
  ts: number;
  totalMappings: number;
  date: string;
}

function GrowthChart({
  data,
  width,
  height,
}: {
  data: KbGrowthPoint[];
  width: number;
  height: number;
}) {
  const margin = { top: 8, right: 12, bottom: 26, left: 36 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const parsed = useMemo<Parsed[]>(
    () => data.map((d) => ({ ...d, ts: new Date(d.date).getTime() })),
    [data]
  );

  const xScale = useMemo(() => {
    const ts = parsed.map((p) => p.ts);
    return scaleTime<number>({ range: [0, innerW], domain: [Math.min(...ts), Math.max(...ts)] });
  }, [parsed, innerW]);

  const yScale = useMemo(() => {
    const max = Math.max(...parsed.map((p) => p.totalMappings));
    return scaleLinear<number>({ range: [innerH, 0], domain: [0, max * 1.1], nice: true });
  }, [parsed, innerH]);

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<Parsed>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
    detectBounds: true,
  });
  const bisectTs = useMemo(() => bisector<Parsed, number>((d) => d.ts).center, []);

  const hoverX = tooltipData ? (xScale(tooltipData.ts) ?? 0) : null;
  const hoverY = tooltipData ? (yScale(tooltipData.totalMappings) ?? 0) : null;

  // Target y-values (the "grown-up" state). Paths are animated by
  // interpolating each data point's y-value from a baseline to here.
  const targetValues = useMemo(() => parsed.map((p) => p.totalMappings), [parsed]);

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <svg width={width} height={height}>
        <LinearGradient
          id="kb-growth-fill"
          from="var(--color-brand-primary)"
          to="var(--color-brand-primary)"
          fromOpacity={0.35}
          toOpacity={0.02}
        />
        <Group top={margin.top} left={margin.left}>
          <AnimatedSeries
            parsed={parsed}
            targetValues={targetValues}
            xScale={xScale}
            yScale={yScale}
            innerH={innerH}
            fillId="kb-growth-fill"
            strokeColour="var(--color-brand-primary-dark)"
          />
          {hoverX !== null && hoverY !== null && (
            <>
              <line
                x1={hoverX}
                x2={hoverX}
                y1={0}
                y2={innerH}
                stroke="var(--color-border)"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
              <circle
                cx={hoverX}
                cy={hoverY}
                r={4}
                fill="var(--color-brand-primary-dark)"
                stroke="white"
                strokeWidth={1.5}
              />
            </>
          )}
          <rect
            x={0}
            y={0}
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={(e) => {
              const pt = (
                e.currentTarget.ownerSVGElement as SVGSVGElement | null
              )?.getBoundingClientRect();
              if (!pt) return;
              const localX = e.clientX - pt.left - margin.left;
              const ts = xScale.invert(localX).valueOf();
              const idx = bisectTs(parsed, ts);
              const point = parsed[idx];
              if (!point) return;
              showTooltip({
                tooltipData: point,
                tooltipLeft: e.clientX - pt.left,
                tooltipTop: e.clientY - pt.top,
              });
            }}
            onMouseLeave={hideTooltip}
          />
          <AxisBottom
            top={innerH}
            scale={xScale}
            numTicks={Math.min(6, Math.floor(innerW / 80))}
            hideAxisLine
            tickStroke="transparent"
            tickLabelProps={() => ({
              fill: "var(--color-text-tertiary)",
              fontSize: 11,
              textAnchor: "middle",
              fontFamily: "var(--font-sans)",
            })}
          />
          <AxisLeft
            scale={yScale}
            numTicks={4}
            hideAxisLine
            tickStroke="transparent"
            tickLabelProps={() => ({
              fill: "var(--color-text-tertiary)",
              fontSize: 11,
              textAnchor: "end",
              dx: -4,
              dy: 3,
              fontFamily: "var(--font-sans)",
            })}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          <div className="font-medium">
            {new Date(tooltipData.ts).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
          <div
            className="text-[var(--color-text-secondary)]"
            style={{ fontFamily: "var(--font-mono)" }}
          >
            {tooltipData.totalMappings.toLocaleString()} mappings
          </div>
        </TooltipInPortal>
      )}
    </div>
  );
}

/**
 * Area + line that grow upward from the x-axis on mount, and glide on data
 * updates.
 *
 * Per frame, interpolates each data point's y-value from the previously
 * displayed value to the new target, regenerates the line and area path
 * strings via d3-shape, and writes the `d` attributes via `setAttribute`.
 *
 * Not using `motion.path` with `animate={{d}}` because framer's string
 * interpolation on SVG path data relies on same-topology paths and is
 * unreliable for area curves where the generator output depends on data
 * length. Direct setAttribute is deterministic.
 */
function AnimatedSeries({
  parsed,
  targetValues,
  xScale,
  yScale,
  innerH,
  fillId,
  strokeColour,
  durationMs = 900,
}: {
  parsed: Parsed[];
  targetValues: number[];
  xScale: ReturnType<typeof scaleTime<number>>;
  yScale: ReturnType<typeof scaleLinear<number>>;
  innerH: number;
  fillId: string;
  strokeColour: string;
  durationMs?: number;
}) {
  const areaRef = useRef<SVGPathElement>(null);
  const lineRef = useRef<SVGPathElement>(null);
  // Start at zero so the area grows from the x-axis on mount.
  const prevValuesRef = useRef<number[]>(parsed.map(() => 0));

  useEffect(() => {
    const areaEl = areaRef.current;
    const lineEl = lineRef.current;
    if (!areaEl || !lineEl) return;

    // Pad prev to match new length (rare: different filter windows).
    const prev = prevValuesRef.current.slice(0, targetValues.length);
    while (prev.length < targetValues.length) prev.push(0);
    const from = prev;
    const to = targetValues;

    const generate = (values: number[]) => {
      const points = parsed.map((p, i) => ({ ...p, totalMappings: values[i] ?? 0 }));
      const lineGen = d3line<Parsed>()
        .x((d) => xScale(d.ts) ?? 0)
        .y((d) => yScale(d.totalMappings) ?? 0)
        .curve(curveMonotoneX);
      const areaGen = d3area<Parsed>()
        .x((d) => xScale(d.ts) ?? 0)
        .y0(innerH)
        .y1((d) => yScale(d.totalMappings) ?? 0)
        .curve(curveMonotoneX);
      return { line: lineGen(points) ?? "", area: areaGen(points) ?? "" };
    };

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      prevValuesRef.current = [...to];
      const { line, area } = generate(to);
      lineEl.setAttribute("d", line);
      areaEl.setAttribute("d", area);
      return;
    }

    let startTime: number | null = null;
    let rafId = 0;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
      if (startTime === null) startTime = now;
      const t = Math.min(1, (now - startTime) / durationMs);
      const eased = ease(t);
      const current = to.map((v, i) => (from[i] ?? 0) + (v - (from[i] ?? 0)) * eased);
      prevValuesRef.current = current;
      const { line, area } = generate(current);
      lineEl.setAttribute("d", line);
      areaEl.setAttribute("d", area);
      if (t < 1) rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [parsed, targetValues, xScale, yScale, innerH, durationMs]);

  return (
    <>
      <path ref={areaRef} fill={`url(#${fillId})`} />
      <path ref={lineRef} stroke={strokeColour} strokeWidth={2} fill="none" />
    </>
  );
}
